import React, { useMemo } from 'react'
import { Button } from 'transfix-ui/components/Button'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { Box, Typography } from 'transfix-ui/core'
import { useELDIntegration, useFMSRoutes, useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { useMembershipValidation } from 'apps/fleet-planner/hooks/useMembershipValidation'
import { KeepTruckinTruckLocations, useKeepTruckinTruckLocations } from 'apps/fleet-planner/types'
import {
  ImportedTruckList,
  useImportTruckListForm,
  ImportTruckListProvider,
} from '../TruckListImport'
import { EldIntegrationStepContainer } from './EldIntegrationStepContainer'

/**
 * Maps KeepTruckin locations to correct type for import request
 */
const mapTruckLocation = <T extends Pick<KeepTruckinTruckLocations, 'number' | 'id' | 'vin'>>({
  id,
  number,
  vin,
}: T): {
  number?: string
  id?: number
  vin?: string
} => ({
  number: number ?? undefined,
  id,
  vin: vin ?? undefined,
})

/**
 * Gets filtered/mapped array of trucks for request
 */
const getSelectedTrucks = (truckIds: string[], truckLocations: KeepTruckinTruckLocations[]) => {
  return truckLocations
    .filter(({ id }) => {
      const isSelected = truckIds.includes(String(id))
      return isSelected
    })
    .map(mapTruckLocation)
}

/**
 * Step 2 of 2: "Import your ELD-connected trucks"
 */
export const EldIntegrationStepImportTrucks = () => {
  const { trackEvent } = useFMSAnalytics()
  const { getErrorState, getLoadingState, importTrucksHandler, params } = useELDIntegration()
  const { carrierId } = params || {}
  const { findMotorCarrierByProp } = useMembershipValidation()
  const history = useFMSRoutes()

  const selectedMotorCarrier = useMemo(() => findMotorCarrierByProp('id', Number(carrierId)), [
    carrierId,
    findMotorCarrierByProp,
  ])
  const { motorCarrier } = selectedMotorCarrier || {}

  const { data: truckLocationData } = useKeepTruckinTruckLocations({})
  const { truckLocations = [] } = truckLocationData ?? {}

  const { name: carrierName = 'PLACEHOLDER CARRIER NAME' } = motorCarrier || {}
  const truckCount = `${truckLocations.length} truck${
    truckLocations.length === 0 || truckLocations.length > 1 ? 's' : ''
  }`

  /**
   * Sets up state which is shared with children list component via context
   */
  const ImportTruckFormContext = useImportTruckListForm({
    defaultValues: truckLocations,
  })

  const clickFooterHandler = async () => {
    trackEvent('ELD', 'CLICKED_FINALIZE_IMPORT_BUTTON')
    // if there are no trucks to import, just go to home page
    if (!truckLocations?.length) {
      history.push('/fleet?importTrucks=success')
    }

    const { selectedTruckIds = [] } = ImportTruckFormContext || {}
    const selectedTrucks = getSelectedTrucks(selectedTruckIds, truckLocations)

    try {
      const res = await importTrucksHandler({
        motorCarrierId: Number(carrierId),
        trucks: selectedTrucks,
      })
      const { trucks = [] } = res || {}
      if (trucks) {
        trackEvent('ELD', 'IMPORT_TRUCKS_SUCCESS')
        trackEvent('ELD', 'KEEPTRUCKIN_AUTH_SUCCESS')
        history.push('/fleet?importTrucks=success')
      }
    } catch (e) {
      trackEvent('ELD', 'IMPORT_TRUCKS_FAILED')
    }
  }

  const importError = getErrorState('importTrucksError')

  return (
    <EldIntegrationStepContainer
      title='Import your ELD-connected trucks'
      subtitle='When you manage a load for this carrier, you’ll be able to assign any of the trucks you choose below and track their location.'
      pageText='Step 2 of 2'
      footer={
        <Button
          color='primary'
          onClick={clickFooterHandler}
          data-test='button-finalize-import'
          disabled={getLoadingState('importTrucksLoading')}>
          Finalize Import
        </Button>
      }>
      {importError && (
        <Box mb={3}>
          <InlineMessage
            type={INLINE_MESSAGE_VARIANTS.ALERT}
            message='There was an error importing your trucks to KeepTruckin. Please try again.'
          />
        </Box>
      )}
      <Box pb={2}>
        <Typography variant='caption' component='div'>
          <strong>{truckCount}</strong> will be imported for <strong>{carrierName}</strong>.
        </Typography>
      </Box>
      <Box>
        <ImportTruckListProvider {...ImportTruckFormContext}>
          <ImportedTruckList
            trucks={truckLocations}
            isLoading={getLoadingState('importTrucksLoading')}
          />
        </ImportTruckListProvider>
      </Box>
    </EldIntegrationStepContainer>
  )
}
